
import React from 'react';
import AnimatedSection from '../../../components/base/AnimatedSection';

const PhilosophySection = () => {
  return (
    <AnimatedSection id="philosophy" className="py-20 px-6 bg-gradient-to-br from-[#A8AE9C]/10 to-[#748881]/10">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-[#394851] mb-6">
            The Pilot Copy's philosophy: Truth Sails Forward
          </h2>
          <p className="text-xl text-[#748881] max-w-3xl mx-auto leading-relaxed">
            Copy is a sacred trust. We believe in clarity over cleverness, authenticity over trend-chasing, 
            and results over awards. We don't manipulate—we understand people. We don't chase trends—we build legacies.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 bg-[#394851] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-compass-line text-2xl text-white"></i>
            </div>
            <h3 className="text-xl font-semibold text-[#394851] mb-4 text-center">
              Truth sails forward
            </h3>
            <p className="text-[#748881] text-center leading-relaxed">
              Like a ship guided by true north, honest copy cuts through the noise and reaches its destination. 
              We navigate by truth, not by the shifting winds of marketing trends.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 bg-[#394851] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-lightbulb-line text-2xl text-white"></i>
            </div>
            <h3 className="text-xl font-semibold text-[#394851] mb-4 text-center">
              Clarity over cleverness
            </h3>
            <p className="text-[#748881] text-center leading-relaxed">
              Your customers don't need to be impressed by fancy words—they need to understand 
              how you solve their problems. Clear communication builds trust and drives action.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 bg-[#394851] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shield-check-line text-2xl text-white"></i>
            </div>
            <h3 className="text-xl font-semibold text-[#394851] mb-4 text-center">
              Authenticity over trend-chasing
            </h3>
            <p className="text-[#748881] text-center leading-relaxed">
              What's trendy today is forgotten tomorrow. But authentic brands that stay true to their 
              values build lasting relationships that weather any storm.
            </p>
          </div>
        </div>

        <div className="bg-[#394851] text-white rounded-lg p-12 mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-semibold mb-8 text-[#A8AE9C]">
              The Pilot Copy Approach
            </h3>
            
            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div>
                <h4 className="text-xl font-semibold mb-4 text-[#A8AE9C]">What we believe:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-[#A8AE9C] mt-1"></i>
                    <span>Results matter more than awards</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-[#A8AE9C] mt-1"></i>
                    <span>Understanding people beats manipulating them</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-[#A8AE9C] mt-1"></i>
                    <span>Building legacies trumps chasing trends</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-[#A8AE9C] mt-1"></i>
                    <span>Copy is a sacred trust between you and your customers</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-xl font-semibold mb-4 text-[#A8AE9C]">What we refuse:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <i className="ri-close-line text-red-400 mt-1"></i>
                    <span>Clever wordplay that confuses instead of clarifies</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-close-line text-red-400 mt-1"></i>
                    <span>Following every marketing fad that comes along</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-close-line text-red-400 mt-1"></i>
                    <span>Psychological tricks that manipulate emotions</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-close-line text-red-400 mt-1"></i>
                    <span>Sacrificing authenticity for short-term gains</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className="bg-white rounded-lg p-8 max-w-4xl mx-auto shadow-lg border-l-4 border-[#A8AE9C]">
            <h3 className="text-2xl font-semibold text-[#394851] mb-4">
              Why this matters for your business
            </h3>
            <p className="text-lg text-[#748881] leading-relaxed mb-6">
              When your copy is built on truth and authenticity, something powerful happens. Your customers 
              don't just buy from you—they believe in you. They become advocates, not just buyers.
            </p>
            <div className="bg-[#394851]/5 rounded-lg p-6">
              <p className="text-[#394851] font-medium italic text-lg">
                "Truth isn't just the right thing to do—it's the profitable thing to do. Authentic brands build lasting value."
              </p>
            </div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

export default PhilosophySection;
